import { environment } from '@environments/environment';
import { Image, Size } from '@shared/components/image/models';
import { getGravity } from '@shared/components/image/utils/get-gravity';
import { getSizes } from '@shared/components/image/utils/get-sizes';
import { LogsService } from '@shared/services/logs';

const CDN_URL: string | undefined = environment.CDN?.url;
const KEEPEEK_INSTANCES = environment.CDN?.keepeekInstances ?? [];

/**
 * Rewrites a Keepeek media URL into its CDN equivalent, appending the transformation segment
 * (format, crop, size) when an image size is requested.
 *
 * @param media - An `Image`, a plain URL string, or a nullish value.
 * @param imageSize - Resize dimensions. Omitted, the returned URL carries no transformation.
 * @param logs - Reports a missing CDN configuration or an unparsable URL.
 *
 * @returns The CDN URL; the input URL untouched when it does not come from Keepeek; an empty
 * string when the media or the CDN configuration is unusable.
 *
 * The alias comes from the instance hosting the media, never from the environment: a page served
 * by the test site can carry an image hosted on the production Keepeek instance.
 */
export const getCDNLink = (
  media: Image | string | null | undefined,
  imageSize?: Size,
  logs?: Pick<LogsService, 'warn' | 'error' | 'trace'>
): string => {
  if (!media || !CDN_URL) {
    if (!CDN_URL) {
      logs?.warn('getCDNLink', 'CDN environment variable (url) is not configured.');
    }
    return '';
  }

  const link = typeof media === 'string' ? media : media.link;
  if (!link) {
    return '';
  }

  const keepeek = KEEPEEK_INSTANCES.find(({ url }) => link.startsWith(url));
  if (!keepeek) {
    return link;
  }

  const href = link.replace(keepeek.url, `${CDN_URL}/${keepeek.alias}`);

  if (!imageSize) {
    return href;
  }

  const params: string[] = ['format=auto', 'fit=cover'];

  if (typeof media !== 'string' && media.gravity) {
    const gravityValue = getGravity(media.gravity);
    if (gravityValue) params.push(gravityValue);
  }

  const sizeValue = getSizes(imageSize);
  if (sizeValue) params.push(sizeValue);

  const transformSegment = `/cdn-cgi/image/${params.join(',')}`;

  try {
    const url = new URL(href);
    const result = `${url.origin}${transformSegment}${url.pathname}${url.search}${url.hash}`;
    logs?.trace('getCDNLink', `Converted to CDN URL`, {
      input: link,
      output: result,
    });
    return result;
  } catch {
    logs?.error('getCDNLink', `Failed to parse URL: ${href}`);
    return href;
  }
};
